// frontend/src/components/Profile.js
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Flag } from "lucide-react";
import HamburgerMenu from "../components/HamburgerMenu";
import PageHeader from "../components/PageHeader";
import ConfirmModal from "../components/ConfirmModal";
import { supabase } from "../supabaseClient";
import { fetchWithAuth } from "../utils/api";
import { linkify } from "../utils/text";
import AlertPreferences from "../components/AlertPreferences";
import BadgeMedal from "../components/BadgeMedal";
import { BADGES, RANKS, rankForPoints, CATEGORY_COLORS, TOTAL_POINTS } from "../badges";
import { SPECIAL_CERT_LABELS } from "../utils/certs";
import { TOTEMS, totemUrl } from "../utils/totems";
import "./Profile.css";

const toArray = (v) =>
  Array.isArray(v) ? v : typeof v === "string" ? v.split(/[,;]\s*/).filter(Boolean) : [];

const CERT_KEYS = [
  "open_water", "advanced_open_water", "rescue", "nitrox", "advanced_nitrox",
  "deep", "divemaster", "instructor", "ccr", "trimix_normo", "trimix_hypo",
  "cavern", "intro_cave", "full_cave"
];

const Profile = ({ currentUser }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // Fonction pour obtenir le label traduit d'une certification
  const getCertLabel = (key) => {
    if (SPECIAL_CERT_LABELS[key]) return SPECIAL_CERT_LABELS[key];
    return t(`certifications.${key}`, key);
  };

  const CERT_OPTIONS = CERT_KEYS.map((key) => ({ key, label: getCertLabel(key) }));
  const { user_id } = useParams(); // détecte si on visite /profile/:id
  const [publicUser, setPublicUser] = useState(null);
  const [profileClubs, setProfileClubs] = useState([]);
  const [badgeMetrics, setBadgeMetrics] = useState(null);
  const [badgeStats, setBadgeStats] = useState({ earnedAt: {}, holders: {}, totalUsers: 0 }); // date + nb de détenteurs + total users
  const [showAllBadges, setShowAllBadges] = useState(false);
  const [showRanks, setShowRanks] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState(null); // { b, val, earned }
  const [myTitle, setMyTitle] = useState(null);        // titre affiché choisi (clé de rang) — profil perso
  const [myTitleGender, setMyTitleGender] = useState("n"); // forme grammaticale du titre (m/f/n)
  const [friends, setFriends] = useState([]);
  const [friendToRemove, setFriendToRemove] = useState(null); // { friendshipId, name } → modale de confirmation
  const [blockedIds, setBlockedIds] = useState([]); // personnes que j'ai bloquées (modération)
  const [confirm, setConfirm] = useState(null); // modale de confirmation (blocage / signalement)
  const apiUrl = process.env.REACT_APP_API_URL;

  // Retire un ami après confirmation (modale stylée, remplace window.confirm)
  const confirmRemoveFriend = async () => {
    if (!friendToRemove) return;
    const { friendshipId } = friendToRemove;
    try {
      const res = await fetchWithAuth(`${apiUrl}/friends/${friendshipId}`, { method: "DELETE" });
      if (!res.ok) throw new Error(t('friends.toasts.friendRemoveError'));
      toast.success(t('friends.toasts.friendRemoved'));
      setFriends((prev) => prev.filter((f) => f.id !== friendshipId));
    } catch (err) {
      console.error(err);
      toast.error(t('friends.toasts.friendRemoveError'));
    } finally {
      setFriendToRemove(null);
    }
  };

  // --- Modération : bloquer / débloquer / signaler un profil (exigence App Store 1.2)
  const performBlock = async (targetId, name) => {
    try {
      const res = await fetchWithAuth(`${apiUrl}/block`, {
        method: "POST",
        body: JSON.stringify({ blocked_id: targetId }),
      });
      if (!res.ok) throw new Error("block failed");
      setBlockedIds((prev) => (prev.includes(targetId) ? prev : [...prev, targetId]));
      toast.success(t('moderation.blocked', { name }));
    } catch {
      toast.error(t('moderation.blockError'));
    }
  };
  const handleUnblock = async (targetId, name) => {
    try {
      const res = await fetchWithAuth(`${apiUrl}/block/${targetId}`, { method: "DELETE" });
      if (!res.ok) throw new Error("unblock failed");
      setBlockedIds((prev) => prev.filter((id) => id !== targetId));
      toast.success(t('moderation.unblocked', { name }));
    } catch {
      toast.error(t('moderation.blockError'));
    }
  };
  const performReportProfile = async (targetId) => {
    try {
      const res = await fetchWithAuth(`${apiUrl}/report`, {
        method: "POST",
        body: JSON.stringify({ reported_user_id: targetId, context: "profile" }),
      });
      if (!res.ok) throw new Error("report failed");
      toast.success(t('moderation.reported'));
    } catch {
      toast.error(t('moderation.reportError'));
    }
  };
  const askBlock = (targetId, name) => setConfirm({
    message: t('moderation.blockConfirm', { name }),
    confirmLabel: t('moderation.block'),
    danger: true,
    onConfirm: () => performBlock(targetId, name),
  });
  const askReportProfile = (targetId) => setConfirm({
    message: t('moderation.reportConfirm'),
    confirmLabel: t('moderation.report'),
    danger: true,
    onConfirm: () => performReportProfile(targetId),
  });

  const [allUsers, setAllUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [currentUserDescription, setCurrentUserDescription] = useState(
    currentUser?.description || ""
  );
  const [selectedCerts, setSelectedCerts] = useState([]);
  const [savingCerts, setSavingCerts] = useState(false);
  const [editingCerts, setEditingCerts] = useState(false);
  const [editingDesc, setEditingDesc] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameFields, setNameFields] = useState({
    first_name: currentUser?.user_metadata?.first_name || "",
    last_name: currentUser?.user_metadata?.last_name || "",
  });
  const [currentTotem, setCurrentTotem] = useState(null);
  const [editingTotem, setEditingTotem] = useState(false);
  const [isSupporter, setIsSupporter] = useState(false);

  const [profileImage, setProfileImage] = useState(currentUser?.profile_image_url || "");

  const [showImageModal, setShowImageModal] = useState(false);

  const [zoomedImage, setZoomedImage] = useState(null);

  const [pendingReceived, setPendingReceived] = useState([]); // demandes reçues
  // Tient la pastille "Profil" de la BottomNav à jour (accept/refus d'une demande d'ami).
  useEffect(() => { window.dispatchEvent(new Event("friend-requests-changed")); }, [pendingReceived]);
  const [pendingSent, setPendingSent] = useState([]); // demandes envoyées par moi
  const [friendSearch, setFriendSearch] = useState("");
  const [showAllFriends, setShowAllFriends] = useState(false);
  const [showFriendsList, setShowFriendsList] = useState(false);

  // Clubs du profil consulté (soi-même ou profil public)
  useEffect(() => {
    const targetId = user_id || currentUser?.id;
    if (!targetId) return;
    fetchWithAuth(`${apiUrl}/users/${targetId}/clubs`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setProfileClubs(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [user_id, currentUser?.id, apiUrl]);

  // Métriques badges du profil consulté
  useEffect(() => {
    const targetId = user_id || currentUser?.id;
    if (!targetId) return;
    fetchWithAuth(`${apiUrl}/badges/${targetId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setBadgeMetrics(d))
      .catch(() => {});
    fetchWithAuth(`${apiUrl}/badge-stats/${targetId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setBadgeStats({ earnedAt: d.earnedAt || {}, holders: d.holders || {}, totalUsers: d.totalUsers || 0 }); })
      .catch(() => {});
  }, [user_id, currentUser?.id, apiUrl]);

  // Changer le titre affiché sur mon profil public (clé de rang, ou "" = aucun).
  const handleTitleChange = async (val) => {
    const prev = myTitle;
    setMyTitle(val || null);
    try {
      const res = await fetchWithAuth(`${apiUrl}/update-title`, { method: "POST", body: JSON.stringify({ title: val || null }) });
      if (!res.ok) throw new Error();
    } catch {
      setMyTitle(prev); // rollback si échec
    }
  };

  // Rendu de la section Badges. isPublic = profil de quelqu'un d'autre → seulement les obtenus.
  const renderBadgesSection = (isPublic) => {
    const m = badgeMetrics || {};
    const withState = BADGES.map((b) => ({ b, val: m[b.metric] || 0, earned: (m[b.metric] || 0) >= b.target }));
    const earned = withState.filter((x) => x.earned);
    // public : obtenus seulement. privé : obtenus par défaut, tous si "voir tout".
    const list = isPublic ? earned : (showAllBadges ? withState : earned);
    // Points cumulés + rang (jauge de profondeur).
    const earnedPts = earned.reduce((s, x) => s + (x.b.points || 0), 0);
    const { current: rank, next: nextRank, nextMin, pct } = rankForPoints(earnedPts);
    const renderBtn = ({ b, val, earned: e }) => (
      <button
        key={b.key}
        onClick={() => setSelectedBadge({ b, val, earned: e })}
        style={{ textAlign: "center", background: "none", border: "none", cursor: "pointer", padding: 0 }}
      >
        <div style={{ display: "flex", justifyContent: "center" }}><BadgeMedal badge={b} earned={e} size={52} /></div>
        <div style={{ fontSize: 11.5, fontWeight: 700, color: e ? "#223344" : "#8a97a3", marginTop: 6, lineHeight: 1.2, minHeight: 28, display: "flex", alignItems: "center", justifyContent: "center" }}>{t(`badges.items.${b.key}.name`)}</div>
        {!isPublic && !e && b.target > 1 && (
          <div style={{ fontSize: 10.5, color: "#9aa7b0", marginTop: 2 }}>{Math.min(val, b.target)}/{b.target}</div>
        )}
      </button>
    );
    return (
      <div className="profile-section" style={{ textAlign: "left" }}>
        <h4 style={{ color: "var(--primary-blue)", margin: 0, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <span>{t('badges.title')}</span>
          <span style={{ fontSize: 14, color: "#777", fontWeight: 600 }}>{earned.length}/{BADGES.length}</span>
        </h4>

        {/* Rang du plongeur (jauge de profondeur) */}
        <div style={{ margin: "10px 0 14px", padding: "12px 14px", borderRadius: 12, background: "linear-gradient(180deg, #eaf4fb, #d7e8f4)", border: "1px solid #cfe0ee" }}>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, color: "var(--primary-blue-dark)", fontSize: "1.05rem" }}>{t(`ranks.${rank.key}`)}</div>
          <div style={{ marginTop: 8, height: 10, borderRadius: 999, background: "#c3d6e4", overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${pct}%`, background: "linear-gradient(90deg, #48cae4, #0077b6 70%, #023e8a)", borderRadius: 999, transition: "width .6s ease" }} />
          </div>
          <div style={{ marginTop: 6, fontSize: 12, color: "#55707e" }}>
            <strong style={{ color: "var(--primary-blue-dark)" }}>{earnedPts} / {TOTAL_POINTS} pts</strong>{" · "}{nextRank ? t('ranks.toNext', { pts: Math.max(0, nextMin - earnedPts), rank: t(`ranks.${nextRank.key}`) }) : t('ranks.max')}
          </div>
          <button onClick={() => setShowRanks((v) => !v)} style={{ marginTop: 8, background: "none", border: "none", color: "var(--primary-blue)", fontWeight: 700, fontSize: 12, cursor: "pointer", padding: 0 }}>
            {showRanks ? t('ranks.hideAll') : t('ranks.showAll')}
          </button>
          {showRanks && (
            <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 3 }}>
              {RANKS.map((r) => {
                const reached = earnedPts >= r.min;
                const isCurrent = r.key === rank.key;
                return (
                  <div key={r.key} style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, padding: "3px 8px", borderRadius: 6, background: isCurrent ? "rgba(0,119,182,0.14)" : "transparent", color: reached ? "#334455" : "#9aa7b0", fontWeight: isCurrent ? 800 : 600 }}>
                    <span>{t(`ranks.${r.key}`)}</span>
                    <span style={{ fontVariantNumeric: "tabular-nums", color: reached ? "#55707e" : "#b3bec6" }}>{r.min} pts</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {/* Titre affichable sur le profil public (choisi parmi les rangs atteints) */}
        {!isPublic && (
          <div style={{ margin: "0 0 14px" }}>
            <label style={{ display: "block", fontSize: 12.5, fontWeight: 700, color: "#55707e", marginBottom: 6 }}>{t('titles.label')}</label>
            <select
              value={myTitle || ""}
              onChange={(e) => handleTitleChange(e.target.value)}
              style={{ width: "100%", padding: "9px 10px", borderRadius: 10, border: "1px solid #cfe0ee", background: "#fff", color: "#334455", fontSize: 14, fontWeight: 600 }}
            >
              <option value="">{t('titles.none')}</option>
              {RANKS.filter((r) => earnedPts >= r.min).map((r) => (
                <option key={r.key} value={r.key}>{t(`titles.${r.key}.${myTitleGender}`)}</option>
              ))}
            </select>
            <div style={{ fontSize: 11.5, color: "#8a97a3", marginTop: 5 }}>{t('titles.help')}</div>
          </div>
        )}
        {!isPublic && <p style={{ color: "#777", fontSize: 13, lineHeight: 1.5, marginTop: 0 }}>{t('badges.intro')}</p>}
        {list.length === 0 ? (
          <p style={{ color: "#8a97a3", fontSize: 13 }}>{t('badges.none')}</p>
        ) : showAllBadges ? (
          // Vue « Voir tout » : catalogue complet regroupé par catégorie.
          ["dives", "explore", "social", "contrib", "variety"].map((cat) => {
            const items = list.filter((x) => x.b.cat === cat);
            if (!items.length) return null;
            return (
              <div key={cat} style={{ marginTop: 10 }}>
                <div style={{ fontSize: 11.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: CATEGORY_COLORS[cat] || "var(--primary-blue)", margin: "6px 0" }}>{t(`badges.categories.${cat}`)}</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(84px, 1fr))", gap: 12 }}>
                  {items.map(renderBtn)}
                </div>
              </div>
            );
          })
        ) : (
          // Vue par défaut : badges obtenus en grille compacte, sans catégories.
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(84px, 1fr))", gap: 12, marginTop: 10 }}>
            {list.map(renderBtn)}
          </div>
        )}
        {!isPublic && (
          <div style={{ textAlign: "center", marginTop: 12 }}>
            <button
              onClick={() => setShowAllBadges((v) => !v)}
              style={{ background: "none", border: "none", color: "var(--primary-blue)", fontWeight: 700, fontSize: 13, cursor: "pointer" }}
            >
              {showAllBadges ? t('badges.showLess') : t('badges.showAll', { count: BADGES.length })}
            </button>
          </div>
        )}

        {selectedBadge && (
          <div onClick={() => setSelectedBadge(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10002, padding: 16 }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, padding: 24, maxWidth: 300, textAlign: "center", boxShadow: "0 12px 40px rgba(0,0,0,0.25)" }}>
              <div style={{ display: "flex", justifyContent: "center" }}><BadgeMedal badge={selectedBadge.b} earned={selectedBadge.earned} size={72} /></div>
              <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "1.15rem", color: "#223344", marginTop: 12 }}>{t(`badges.items.${selectedBadge.b.key}.name`)}</div>
              <div style={{ color: "#556", fontSize: 14, marginTop: 6, lineHeight: 1.5 }}>{t(`badges.items.${selectedBadge.b.key}.desc`)}</div>
              <div style={{ marginTop: 12, fontWeight: 700, fontSize: 13, color: selectedBadge.earned ? "#2e9e5b" : "var(--primary-blue)" }}>
                {selectedBadge.earned ? t('badges.earned') : `${Math.min(selectedBadge.val, selectedBadge.b.target)} / ${selectedBadge.b.target}`}
              </div>
              {selectedBadge.earned && badgeStats.earnedAt[selectedBadge.b.key] && (
                <div style={{ marginTop: 6, fontSize: 12, color: "#8a97a3" }}>
                  {t('badges.earnedOn', { date: new Date(badgeStats.earnedAt[selectedBadge.b.key]).toLocaleDateString() })}
                </div>
              )}
              {(() => {
                const count = badgeStats.holders[selectedBadge.b.key] || 0;
                const total = badgeStats.totalUsers || 0;
                if (total === 0) return null;
                const pct = Math.round((count / total) * 100);
                return <div style={{ marginTop: 4, fontSize: 12, color: "#8a97a3" }}>{t('badges.rarity', { count, total, pct })}</div>;
              })()}
              <button onClick={() => setSelectedBadge(null)} style={{ marginTop: 16, background: "var(--primary-blue)", color: "#fff", border: "none", borderRadius: 10, padding: "8px 18px", fontWeight: 700, cursor: "pointer" }}>{t('common.close')}</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Rendu de la section Clubs (chips cliquables vers la page du club)
  const renderClubsSection = () => {
    if (!profileClubs || profileClubs.length === 0) return null;
    const clubColors = ["#0077b6", "#0096c7", "#2e9e5b", "#e0a400", "#8a6d3b", "#6f4a9e", "#c0507a"];
    const colorFor = (name) => { let h = 0; for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0; return clubColors[h % clubColors.length]; };
    return (
      <div className="profile-section" style={{ textAlign: "left" }}>
        <h4 style={{ color: "var(--primary-blue)", margin: 0 }}>{t('clubs.title')}</h4>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "flex-start", marginTop: 10 }}>
          {profileClubs.map((c) => (
            <button
              key={c.id}
              onClick={() => navigate(`/clubs/${c.id}`)}
              style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#fff", border: "1px solid #e6edf3", borderRadius: 999, padding: "5px 12px 5px 6px", cursor: "pointer", fontSize: 14, fontWeight: 600, color: "#223344" }}
            >
              {c.logo_url ? (
                <img src={c.logo_url} alt="" style={{ width: 26, height: 26, borderRadius: 8, objectFit: "cover" }} />
              ) : (
                <span style={{ width: 26, height: 26, borderRadius: 8, background: colorFor(c.name), color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 13 }}>{(c.name || "?").charAt(0).toUpperCase()}</span>
              )}
              {c.name}
            </button>
          ))}
        </div>
      </div>
    );
  };


  const handleLogout = async () => {
    // signOut() révoque le refresh token côté serveur et retire le token
    // d'auth du localStorage, sans toucher aux préférences de langue.
    try {
      await supabase.auth.signOut();
    } catch (err) {
      console.error("Erreur lors de la déconnexion :", err);
    }
    navigate("/");
    window.location.reload();
  };

  const handleAbout = () => {
    navigate("/about");
  };

  const handleGoToProfile = () => {
    navigate(`/profile/${currentUser.id}`);
  };

  const [publicUserFriends, setPublicUserFriends] = useState([]);

  // Si user_id existe → c'est un profil public
  const isPublicProfile = Boolean(user_id);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        if (isPublicProfile) {
          const res = await fetchWithAuth(`${apiUrl}/users/${user_id}`);
          const data = await res.json();
          setPublicUser(data);

          // Charger les amis de la personne visitée (pour afficher sa liste d'amis)
          const publicUserFriendsRes = await fetchWithAuth(`${apiUrl}/friends/${user_id}`);
          const publicUserFriendsData = await publicUserFriendsRes.json();
          setPublicUserFriends(Array.isArray(publicUserFriendsData) ? publicUserFriendsData : []);

          // Charger mes amis et demandes pour savoir l'état de la relation
          if (currentUser?.id) {
            const myFriendsRes = await fetchWithAuth(`${apiUrl}/friends/${currentUser.id}`);
            const myFriendsData = await myFriendsRes.json();
            setFriends(Array.isArray(myFriendsData) ? myFriendsData : []);

            // Charger mes demandes envoyées et reçues
            const pendingReceivedRes = await fetchWithAuth(`${apiUrl}/friend-requests/received/${currentUser.id}`);
            const pendingReceivedData = await pendingReceivedRes.json();
            setPendingReceived(Array.isArray(pendingReceivedData) ? pendingReceivedData : []);

            const pendingSentRes = await fetchWithAuth(`${apiUrl}/friend-requests/sent/${currentUser.id}`);
            const pendingSentData = await pendingSentRes.json();
            setPendingSent(Array.isArray(pendingSentData) ? pendingSentData : []);

            // Qui ai-je bloqué (pour afficher Bloquer / Débloquer)
            try {
              const blocksRes = await fetchWithAuth(`${apiUrl}/blocks`);
              const blocksData = await blocksRes.json();
              setBlockedIds(Array.isArray(blocksData) ? blocksData : []);
            } catch { /* non bloquant */ }
          }
        } else if (currentUser?.id) {
          // Profil connecté
          // Charger la description actuelle pour le profil privé
          const userRes = await fetchWithAuth(`${apiUrl}/users/${currentUser.id}`);
          const userData = await userRes.json();

          setSelectedCerts(toArray(userData?.certifications));

          if (userData?.member_number) {
            currentUser.member_number = userData.member_number;
          }

          if (userData?.description) setCurrentUserDescription(userData.description);

          if (userData?.first_name || userData?.last_name) {
            setNameFields({ first_name: userData.first_name || "", last_name: userData.last_name || "" });
          }

          setCurrentTotem(userData?.totem || null);
          setIsSupporter(userData?.supporter === true);
          setMyTitle(userData?.displayed_title || null);
          setMyTitleGender(userData?.title_gender || "n");

          if (userData?.profile_image_url) setProfileImage(userData.profile_image_url);

          // Charger les amis de l'utilisateur connecté
          const friendsRes = await fetchWithAuth(`${apiUrl}/friends/${currentUser.id}`);
          const friendsData = await friendsRes.json();
          setFriends(Array.isArray(friendsData) ? friendsData : []);

          // Charger les demandes d'amis reçues (pending)
          const pendingReceivedRes = await fetchWithAuth(`${apiUrl}/friend-requests/received/${currentUser.id}`);
          const pendingReceivedData = await pendingReceivedRes.json();
          setPendingReceived(Array.isArray(pendingReceivedData) ? pendingReceivedData : []);

          // Charger les demandes envoyées (pour savoir lesquelles sont "en attente")
          const pendingSentRes = await fetchWithAuth(`${apiUrl}/friend-requests/sent/${currentUser.id}`);
          const pendingSentData = await pendingSentRes.json();
          setPendingSent(Array.isArray(pendingSentData) ? pendingSentData : []);

        }
      } catch (error) {
        console.error("Erreur chargement profil :", error);
      }
    };
    loadProfile();
  }, [isPublicProfile, user_id, currentUser, apiUrl]);

  // Charger la liste de tous les utilisateurs pour la recherche d'amis (sur son propre profil)
  useEffect(() => {
    if (!isPublicProfile && currentUser?.id) {
      fetchWithAuth(`${apiUrl}/users`)
        .then((res) => {
          if (!res.ok) throw new Error("Erreur serveur");
          return res.json();
        })
        .then((data) => {
          if (Array.isArray(data)) {
            const others = data.filter((u) => u.id !== currentUser.id);
            setAllUsers(others);
          } else {
            setAllUsers([]);
          }
        })
        .catch((err) => {
          console.error("Erreur chargement utilisateurs :", err);
          setAllUsers([]);
        });
    }
  }, [apiUrl, currentUser, isPublicProfile]);

  // Met à jour la liste selon la recherche
  useEffect(() => {
    const lower = searchTerm.toLowerCase();
    setFilteredUsers(
      allUsers.filter((u) =>
        `${u.first_name} ${u.last_name}`.toLowerCase().includes(lower)
      )
    );
  }, [searchTerm, allUsers]);

  // --- Helpers recherche amis ---
  const friendIds = new Set(
    (friends || [])
      .map((f) => f.friend_id ?? f.friend?.id)
      .filter(Boolean)
  );

  const resultsNotFriends = (filteredUsers || []).filter(
    (u) => u.id !== currentUser?.id && !friendIds.has(u.id)
  );

  const resultsAlreadyFriends = (filteredUsers || []).filter(
    (u) => u.id !== currentUser?.id && friendIds.has(u.id)
  );

  // IDs des demandes envoyées en attente
  const pendingSentIds = new Set(
    (pendingSent || [])
      .filter((r) => r.status === "pending")
      .map((r) => r.recipient_id)
  );

  // IDs des demandes reçues en attente (pour afficher dans la recherche)
  const pendingReceivedIds = new Set(
    (pendingReceived || []).map((r) => r.requester_id)
  );

  const saveTotem = async (key) => {
    const prev = currentTotem;
    setCurrentTotem(key); // optimiste
    try {
      const res = await fetchWithAuth(`${apiUrl}/update-totem`, {
        method: "POST",
        body: JSON.stringify({ totem: key }),
      });
      if (!res.ok) throw new Error("update-totem failed");
      setEditingTotem(false);
    } catch (err) {
      console.error(err);
      setCurrentTotem(prev); // rollback
    }
  };

  const saveName = async () => {
    const first = (nameFields.first_name || "").trim();
    if (!first) { toast.error(t('profile.toasts.nameError')); return; }
    try {
      const res = await fetchWithAuth(`${apiUrl}/update-name`, {
        method: "POST",
        body: JSON.stringify({ first_name: first, last_name: (nameFields.last_name || "").trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('profile.toasts.nameError'));
      // Met aussi à jour les métadonnées auth (lues par le « Bienvenue {prénom} »
      // de la nav) → l'affichage se rafraîchit immédiatement, sans reconnexion.
      await supabase.auth.updateUser({
        data: { first_name: first, last_name: (nameFields.last_name || "").trim() },
      }).catch(() => { /* non bloquant : la table est déjà à jour */ });
      toast.success(t('profile.toasts.nameUpdated'));
      setEditingName(false);
    } catch (err) {
      console.error(err);
      toast.error(err.message || t('profile.toasts.nameError'));
    }
  };

  const saveDescription = async () => {
    try {
      const res = await fetchWithAuth(`${apiUrl}/update-description`, {
        method: "POST",
        body: JSON.stringify({
          description: currentUserDescription,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('profile.toasts.descriptionError'));
      toast.success(t('profile.toasts.descriptionUpdated'));
      setEditingDesc(false);
    } catch (err) {
      console.error(err);
      toast.error(t('profile.toasts.descriptionError'));
    }
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      const res = await fetchWithAuth(`${apiUrl}/friend-requests/${requestId}/accept`, {
        method: "POST",
      });
      if (!res.ok) throw new Error(t('friends.toasts.requestAcceptError'));
      toast.success(t('friends.toasts.requestAccepted'));

      // Recharger les données
      const [friendsRes, pendingRes] = await Promise.all([
        fetchWithAuth(`${apiUrl}/friends/${currentUser.id}`),
        fetchWithAuth(`${apiUrl}/friend-requests/received/${currentUser.id}`),
      ]);
      setFriends(await friendsRes.json());
      setPendingReceived(await pendingRes.json());
    } catch (err) {
      console.error(err);
      toast.error(t('friends.toasts.requestAcceptError'));
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      const res = await fetchWithAuth(`${apiUrl}/friend-requests/${requestId}/reject`, {
        method: "POST",
      });
      if (!res.ok) throw new Error(t('friends.toasts.requestRejectError'));
      toast.success(t('friends.toasts.requestRejected'));

      // Retirer de la liste
      setPendingReceived((prev) => prev.filter((r) => r.id !== requestId));
    } catch (err) {
      console.error(err);
      toast.error(t('friends.toasts.requestRejectError'));
    }
  };

  const handleSendFriendRequest = async (recipientId, recipientName) => {
    try {
      const res = await fetchWithAuth(`${apiUrl}/friend-requests`, {
        method: "POST",
        body: JSON.stringify({
          recipient_id: recipientId,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('friends.toasts.requestSendError'));

      toast.success(t('friends.toasts.requestSent', { name: recipientName }));
      setPendingSent((prev) => [...prev, { recipient_id: recipientId, status: "pending" }]);
    } catch (err) {
      console.error(err);
      toast.error(err.message || t('friends.toasts.requestSendError'));
    }
  };

  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  const handleProfileImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileExt = file.name.split(".").pop();
    const fileName = `${currentUser.id}_${Date.now()}.${fileExt}`;

    // Upload dans Supabase Storage
    const { error: uploadError } = await supabase.storage
      .from("profile-pictures")
      .upload(fileName, file);

    if (uploadError) {
      toast.error(t('profile.toasts.uploadError') + " " + uploadError.message);
      return;
    }

    // Récupération URL publique
    const { data: publicData } = supabase.storage
      .from("profile-pictures")
      .getPublicUrl(fileName);
    const publicUrl = publicData?.publicUrl;

    // Enregistrement de l'URL via le backend (service_role) → compatible RLS.
    try {
      const res = await fetchWithAuth(`${apiUrl}/update-profile-image`, {
        method: "POST",
        body: JSON.stringify({ profile_image_url: publicUrl }),
      });
      if (!res.ok) throw new Error("update failed");

      setProfileImage(publicUrl);
      toast.success(t('profile.toasts.photoUpdated'));
    } catch (err) {
      console.error("Erreur mise à jour photo :", err);
      toast.error(t('profile.toasts.updateError'));
    }
  };

  const toggleCert = (key) => {
    setSelectedCerts((prev) => {
      if (prev.includes(key)) return prev.filter((k) => k !== key);
      return [...prev, key];
    });
  };

  const saveCertifications = async () => {
    try {
      setSavingCerts(true);

      // Passe par le backend (service_role) → compatible avec la RLS sur `users`.
      const res = await fetchWithAuth(`${apiUrl}/update-certifications`, {
        method: "POST",
        body: JSON.stringify({ certifications: selectedCerts }),
      });
      if (!res.ok) throw new Error("save failed");

      toast.success(t('profile.toasts.certificationsSaved'));
      setEditingCerts(false);
    } catch (err) {
      console.error("Erreur save certifications:", err);
      toast.error(t('profile.toasts.certificationsError'));
    } finally {
      setSavingCerts(false);
    }
  };

  // Modale de confirmation "retirer un ami" (partagée entre profil public et privé)
  const friendRemoveModal = friendToRemove && (
    <div onClick={() => setFriendToRemove(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10060, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, padding: 22, maxWidth: 340, width: "100%", textAlign: "center", boxShadow: "0 16px 48px rgba(0,0,0,0.28)" }}>
        <p style={{ margin: "0 0 18px", color: "#334", fontSize: 15, lineHeight: 1.5 }}>
          {t('friends.confirmations.removeFriend', { name: friendToRemove.name })}
        </p>
        <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
          <button onClick={() => setFriendToRemove(null)} style={{ background: "#eef2f4", border: "none", borderRadius: 8, padding: "8px 16px", fontWeight: 600, cursor: "pointer", color: "#556" }}>{t('common.cancel')}</button>
          <button onClick={confirmRemoveFriend} style={{ background: "#e05252", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontWeight: 700, cursor: "pointer" }}>{t('common.remove')}</button>
        </div>
      </div>
    </div>
  );

  // ---------- PROFIL PUBLIC ----------
  if (isPublicProfile && publicUser) {
    return (
      <div
        className="profile-container"
        style={{
          width: "90%",
          maxWidth: "900px",
          margin: "50px auto",
          padding: "20px",
          background: "#f8f9fa",
          borderRadius: "12px",
          boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
          textAlign: "center",
          position: "relative",
          transition: "max-width 0.3s ease",
        }}
      >
        {friendRemoveModal}
        <ConfirmModal state={confirm} onClose={() => setConfirm(null)} />

        <div style={{ position: "absolute", top: "15px", right: "10px", zIndex: 1500 }}>
          <HamburgerMenu
            onLogout={handleLogout}
            onProfileClick={() => navigate("/profile")}
            onAboutClick={handleAbout}
            onMapClick={() => navigate("/")}
            onCommunityClick={() => navigate("/community")}
          />
        </div>


        <div className="page-header-bleed">
          <PageHeader
            title={
              publicUser.first_name || publicUser.last_name
                ? `${publicUser.first_name || ""} ${publicUser.last_name || ""}`
                : t('common.unknownUser')
            }
            subtitle={publicUser?.displayed_title ? t(`titles.${publicUser.displayed_title}.${publicUser.title_gender || "n"}`) : null}
            action={totemUrl(publicUser?.totem) ? (
              <img src={totemUrl(publicUser.totem)} alt="" title={t('profile.totem.title')} style={{ height: 56, width: "auto", maxWidth: 76, objectFit: "contain", marginLeft: "auto", filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.2))" }} />
            ) : null}
          />
        </div>

        <img
          src={publicUser?.profile_image_url || "/default-avatar.png"}
          alt="Photo de profil"
          onClick={() => {
            if (publicUser?.profile_image_url) {
              setZoomedImage(publicUser.profile_image_url);
              setShowImageModal(true);
            }
          }}
          style={{
            width: "120px",
            height: "120px",
            minWidth: "120px",
            minHeight: "120px",
            aspectRatio: "1/1",
            borderRadius: "50%",
            objectFit: "cover",
            margin: "10px auto",
            display: "block",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            cursor: publicUser?.profile_image_url ? "zoom-in" : "default",
          }}
        />

        {/* Bouton d'amitié ou badge */}
        {currentUser?.id !== publicUser?.id && (
          <div style={{ marginTop: "15px", marginBottom: "10px" }}>
            {friends.some((f) => f.friend_id === publicUser.id || f.friend?.id === publicUser.id) ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "12px" }}>
                <span
                  style={{
                    background: "#e8f5e9",
                    color: "#2d6a4f",
                    fontWeight: "600",
                    fontSize: "13px",
                    padding: "6px 14px",
                    borderRadius: "20px",
                  }}
                >
                  {t('friends.badge')}
                </span>
                <button
                  onClick={() => {
                    const friendship = friends.find(
                      (f) => f.friend_id === publicUser.id || f.friend?.id === publicUser.id
                    );
                    if (friendship) {
                      setFriendToRemove({ friendshipId: friendship.id, name: publicUser.first_name || "" });
                    }
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#999",
                    fontSize: "12px",
                    cursor: "pointer",
                    padding: 0,
                    textDecoration: "underline",
                  }}
                >
                  {t('common.remove')}
                </button>
              </div>
            ) : pendingSent.some((r) => r.recipient_id === publicUser.id && r.status === "pending") ? (
              <span
                style={{
                  background: "#f0f0f0",
                  color: "#666",
                  fontSize: "13px",
                  padding: "6px 14px",
                  borderRadius: "20px",
                }}
              >
                {t('friends.pending')}
              </span>
            ) : pendingReceived.some((r) => r.requester_id === publicUser.id) ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
                <span style={{ color: "#555", fontSize: "13px" }}>
                  {t('friends.wantsToBeFriend')}
                </span>
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    onClick={() => {
                      const request = pendingReceived.find((r) => r.requester_id === publicUser.id);
                      if (request) handleAcceptRequest(request.id);
                    }}
                    style={{
                      backgroundColor: "var(--primary-blue)",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      padding: "6px 14px",
                      fontSize: "13px",
                      cursor: "pointer",
                    }}
                  >
                    {t('friends.buttons.accept')}
                  </button>
                  <button
                    onClick={() => {
                      const request = pendingReceived.find((r) => r.requester_id === publicUser.id);
                      if (request) handleRejectRequest(request.id);
                    }}
                    style={{
                      backgroundColor: "transparent",
                      color: "#888",
                      border: "1px solid #ccc",
                      borderRadius: "6px",
                      padding: "6px 14px",
                      fontSize: "13px",
                      cursor: "pointer",
                    }}
                  >
                    {t('friends.buttons.reject')}
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() =>
                  handleSendFriendRequest(
                    publicUser.id,
                    `${publicUser.first_name || ""} ${publicUser.last_name || ""}`.trim()
                  )
                }
                style={{
                  backgroundColor: "var(--primary-blue)",
                  color: "white",
                  border: "none",
                  borderRadius: "20px",
                  padding: "8px 18px",
                  cursor: "pointer",
                  fontSize: "13px",
                  fontWeight: "500",
                }}
              >
                {t('friends.addButton')}
              </button>
            )}
          </div>
        )}

        {/* Modération : bloquer / signaler (exigence App Store 1.2) */}
        {currentUser?.id && publicUser?.id && currentUser.id !== publicUser.id && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "16px", marginBottom: "14px", flexWrap: "wrap" }}>
            {blockedIds.includes(publicUser.id) ? (
              <>
                <span style={{ fontSize: "12px", color: "#c0392b", fontWeight: 600 }}>
                  {t('moderation.blockedBadge')}
                </span>
                <button
                  type="button"
                  onClick={() => handleUnblock(publicUser.id, publicUser.first_name || "")}
                  style={{ background: "none", border: "none", color: "#777", fontSize: "12px", cursor: "pointer", padding: 0, textDecoration: "underline" }}
                >
                  {t('moderation.unblock')}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => askReportProfile(publicUser.id)}
                  style={{ background: "none", border: "none", color: "#999", fontSize: "12px", cursor: "pointer", padding: 0, display: "inline-flex", alignItems: "center", gap: "4px" }}
                >
                  <Flag size={12} /> {t('moderation.reportProfile')}
                </button>
                <button
                  type="button"
                  onClick={() => askBlock(publicUser.id, publicUser.first_name || "")}
                  style={{ background: "none", border: "none", color: "#c0392b", fontSize: "12px", cursor: "pointer", padding: 0 }}
                >
                  {t('moderation.block')}
                </button>
              </>
            )}
          </div>
        )}

        {/* --- Infos (membre / certifs / canton / âge) --- */}
        <div style={{ color: "#555", display: "flex", flexDirection: "column", gap: "14px", marginBottom: "20px" }}>
          {/* Ligne membre n° et date */}
          {publicUser.created_at && (
            <p style={{ margin: 0 }}>
              {publicUser.member_number ? (
                <>
                  {t('profile.labels.memberNumber')}<strong>{publicUser.member_number}</strong> · {t('profile.labels.since')}{" "}
                  {new Date(publicUser.created_at).toLocaleDateString(i18n.language, {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  })}
                </>
              ) : (
                <>
                  {t('profile.labels.memberSince')}{" "}
                  {new Date(publicUser.created_at).toLocaleDateString(i18n.language, {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  })}
                </>
              )}
            </p>
          )}

          {/* Certifications */}
          {(() => {
            const certs = toArray(publicUser.certifications);

            return (
              <div style={{ margin: 0 }}>
                <div>
                  {t('profile.labels.certifications')}{" "}
                  {certs.length === 0 ? <em>{t('common.notProvided')}</em> : null}
                </div>

                {certs.length > 0 && (
                  <div className="certChips" style={{ marginTop: 6 }}>
                    {certs.map((c) => {
                      const key = String(c).trim();
                      const label = getCertLabel(key);
                      return (
                        <span key={key} className="certChip">
                          {label}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Canton */}
          <p style={{ margin: 0 }}>
            {t('profile.labels.canton')} <strong>{publicUser.canton || t('common.notSpecified')}</strong>
          </p>

          {/* Âge */}
          <p style={{ margin: 0 }}>
            {t('profile.labels.age')}{" "}
            {publicUser.birth_date ? (
              <strong>{calculateAge(publicUser.birth_date)} {t('profile.labels.years')}</strong>
            ) : (
              <em>{t('common.notProvided')}</em>
            )}
          </p>
        </div>

        {/* Description */}
        {publicUser.description ? (
          <div
            style={{
              marginBottom: "20px",
              background: "#fff",
              padding: "15px 20px",
              borderRadius: "8px",
              boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
              textAlign: "left",
            }}
          >
            <h4
              style={{
                color: "var(--primary-blue)",
                marginBottom: "8px",
                borderBottom: "1px solid #e0e0e0",
                paddingBottom: "4px",
              }}
            >
              {t('profile.labels.aboutSection')}
            </h4>
            <p
              style={{
                color: "#444",
                fontSize: "15px",
                lineHeight: "1.5",
                whiteSpace: "pre-wrap",
              }}
              dangerouslySetInnerHTML={{ __html: linkify(publicUser.description) }}
            ></p>

          </div>
        ) : (
          <p style={{ color: "#777", fontStyle: "italic", marginBottom: "20px" }}>
            {t('profile.noDescription')}
          </p>
        )}

        {/* Liste des amis de cette personne */}
        {publicUserFriends.length > 0 ? (
          <div
            style={{
              marginBottom: "20px",
              textAlign: "left",
              background: "#fff",
              padding: "15px 20px",
              borderRadius: "8px",
              boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
            }}
          >
            <h4 style={{ color: "var(--primary-blue)", margin: 0, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <span>{t('friends.titlePublic')}</span>
              <span style={{ fontSize: 14, color: "#777", fontWeight: 600 }}>{publicUserFriends.length}</span>
            </h4>
          </div>
        ) : (
          <p style={{ color: "#777", marginTop: "20px" }}>{t('friends.noFriendsPublic')}</p>
        )
        }

        {renderBadgesSection(true)}
        {renderClubsSection()}

        <div className="profile-bottom-spacer" />

        {
          showImageModal && (
            <div
              onClick={() => setShowImageModal(false)}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                background: "rgba(0, 0, 0, 0.8)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 2000,
                cursor: "zoom-out",
              }}
            >
              <img
                src={zoomedImage}
                alt="Photo agrandie"
                style={{
                  maxWidth: "30%",
                  maxHeight: "40%",
                  borderRadius: "12px",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.4)",
                  transition: "transform 0.25s ease",
                }}
              />
            </div>
          )
        }

        <ToastContainer position="bottom-center" autoClose={3000} theme="colored" />
      </div >
    );
  }


  // ---------- PROFIL PERSONNEL ----------
  return (
    <div
      className="profile-container"
      style={{
        width: "90%",
        maxWidth: "900px",
        margin: "40px auto",
        padding: "20px",
        backgroundColor: "#f9f9f9",
        borderRadius: "12px",
        boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
        position: "relative",
        transition: "max-width 0.3s ease", // doux redimensionnement
      }}
    >
      {friendRemoveModal}

      <div style={{ position: "absolute", top: "15px", right: "10px", zIndex: 1500 }}>
        <HamburgerMenu
          onLogout={handleLogout}
          onProfileClick={() => navigate("/profile")}
          onAboutClick={handleAbout}
          onMapClick={() => navigate("/")}
          onCommunityClick={() => navigate("/community")}
        />
      </div>

      <div className="page-header-bleed">
        <PageHeader
          title={t('profile.title')}
          action={
            <button
              type="button"
              onClick={() => navigate(`/profile/${currentUser.id}`)}
              title={t('profile.publicProfile.viewPublic')}
              aria-label={t('profile.publicProfile.viewPublic')}
              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--primary-blue)", display: "flex", alignItems: "center", padding: 4 }}
            >
              <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </button>
          }
        />
      </div>

      {/* Langue + déconnexion, accessibles sur mobile (le hamburger y est masqué) */}
      <div className="mobile-only-settings">
        <select
          value={(i18n.language || "fr").slice(0, 2)}
          onChange={(e) => i18n.changeLanguage(e.target.value)}
          aria-label="Langue"
          style={{ border: "1px solid #cfe0e8", borderRadius: 999, padding: "5px 12px", fontSize: 13, fontWeight: 600, color: "#4a6470", background: "#fff", cursor: "pointer" }}
        >
          <option value="fr">FR</option>
          <option value="de">DE</option>
          <option value="it">IT</option>
          <option value="en">EN</option>
        </select>
        <button type="button" className="mobile-about" onClick={() => navigate("/about")}>
          {t('hamburgerMenu.about')}
        </button>
        <button type="button" className="mobile-logout" onClick={handleLogout}>
          {t('hamburgerMenu.logout')}
        </button>
      </div>


      <div style={{ textAlign: "center", margin: "10px 0" }}>
        <img
          src={profileImage || "/default-avatar.png"}
          alt="Photo de profil"
          onClick={() => {
            if (profileImage) {
              setZoomedImage(profileImage);
              setShowImageModal(true);
            }
          }}
          style={{
            width: "120px",
            height: "120px",
            minWidth: "120px",
            minHeight: "120px",
            aspectRatio: "1/1",
            borderRadius: "50%",
            objectFit: "cover",
            margin: "10px auto",
            display: "block",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            cursor: profileImage ? "zoom-in" : "default",
          }}
        />

        {/* --- Upload customisé de la photo --- */}
        <label
          htmlFor="profile-upload"
          style={{
            display: "inline-block",
            backgroundColor: "#ddd",
            color: "#333",
            borderRadius: "6px",
            padding: "8px 14px",
            cursor: "pointer",
            fontWeight: "bold",
            marginTop: "10px",
            fontSize: "14px",
            transition: "background 0.2s ease",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#ccc")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#ddd")}
        >
          {t('profile.buttons.changePhoto')}
        </label>

        <input
          id="profile-upload"
          type="file"
          accept="image/*"
          onChange={handleProfileImageUpload}
          style={{ display: "none" }}
        />


      </div>

      <p style={{ textAlign: "center", color: "#555" }}>
        {t('profile.labels.email')} <strong>{currentUser?.email}</strong>
      </p>

      {/* --- Nom affiché (éditable) --- */}
      <div style={{ maxWidth: 420, margin: "6px auto 16px" }}>
        {!editingName ? (
          <div style={{ textAlign: "center" }}>
            <span style={{ color: "#555" }}>{t('profile.labels.name')} </span>
            <strong>{`${nameFields.first_name || ""} ${nameFields.last_name || ""}`.trim() || "—"}</strong>
            <button onClick={() => setEditingName(true)} style={{ marginLeft: 8, background: "none", color: "var(--primary-blue)", border: "1px solid var(--primary-blue)", borderRadius: 8, padding: "4px 10px", cursor: "pointer", fontWeight: 600, fontSize: 12 }}>
              {t('common.edit')}
            </button>
          </div>
        ) : (
          <div style={{ background: "#fff", padding: "14px 16px", borderRadius: 10, boxShadow: "0 2px 5px rgba(0,0,0,0.05)" }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <input
                value={nameFields.first_name}
                onChange={(e) => setNameFields((n) => ({ ...n, first_name: e.target.value }))}
                placeholder={t('profile.name.firstName')}
                style={{ flex: "1 1 140px", minWidth: 0, borderRadius: 6, border: "1px solid #ccc", padding: "8px 10px", fontSize: 14, fontFamily: "inherit", boxSizing: "border-box" }}
              />
              <input
                value={nameFields.last_name}
                onChange={(e) => setNameFields((n) => ({ ...n, last_name: e.target.value }))}
                placeholder={t('profile.name.lastName')}
                style={{ flex: "1 1 140px", minWidth: 0, borderRadius: 6, border: "1px solid #ccc", padding: "8px 10px", fontSize: 14, fontFamily: "inherit", boxSizing: "border-box" }}
              />
            </div>
            <p style={{ fontSize: 12, color: "#8a97a3", margin: "8px 0 10px" }}>{t('profile.name.hint')}</p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button onClick={() => setEditingName(false)} style={{ backgroundColor: "#e0e0e0", color: "#333", border: "none", borderRadius: 6, padding: "8px 14px", cursor: "pointer", fontWeight: "bold", fontSize: 14 }}>
                {t('common.cancel')}
              </button>
              <button onClick={saveName} style={{ backgroundColor: "var(--primary-blue)", color: "white", border: "none", borderRadius: 6, padding: "8px 14px", cursor: "pointer", fontWeight: "bold", fontSize: 14 }}>
                {t('common.save')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Affiche le numéro de membre et la date d'inscription */}
      {currentUser?.created_at && (
        <p style={{ textAlign: "center", color: "#555", marginBottom: "20px" }}>
          {currentUser?.member_number ? (
            <>
              {t('profile.labels.memberNumber')}<strong>{currentUser.member_number}</strong> · {t('profile.labels.since')}{" "}
              {new Date(currentUser.created_at).toLocaleDateString(i18n.language, {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })}
            </>
          ) : (
            <>
              {t('profile.labels.memberSince')}{" "}
              {new Date(currentUser.created_at).toLocaleDateString(i18n.language, {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })}
            </>
          )}
        </p>
      )}

      {/* --- Mon totem (cosmétique, réservé aux soutiens) --- */}
      {isSupporter && (
      <div style={{ margin: "6px 0 20px" }}>
        <h4 style={{ color: "var(--primary-blue)", marginBottom: 10, textAlign: "center" }}>{t('profile.totem.title')}</h4>
        {!editingTotem ? (
          <div style={{ textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            {totemUrl(currentTotem) && (
              <img src={totemUrl(currentTotem)} alt="" style={{ height: 34, width: "auto", maxWidth: 44, objectFit: "contain" }} />
            )}
            <span style={{ color: "#555" }}>
              {currentTotem
                ? (TOTEMS.find((x) => x.key === currentTotem)?.name || currentTotem)
                : t('profile.totem.none')}
            </span>
            <button onClick={() => setEditingTotem(true)} style={{ background: "none", color: "var(--primary-blue)", border: "1px solid var(--primary-blue)", borderRadius: 8, padding: "4px 10px", cursor: "pointer", fontWeight: 600, fontSize: 12 }}>
              {t('common.edit')}
            </button>
          </div>
        ) : (
          <div style={{ background: "#fff", padding: "14px 16px", borderRadius: 10, boxShadow: "0 2px 5px rgba(0,0,0,0.05)" }}>
            <p style={{ fontSize: 12, color: "#8a97a3", margin: "0 0 10px", textAlign: "center" }}>{t('profile.totem.hint')}</p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))", gap: 8 }}>
              <button
                onClick={() => saveTotem(null)}
                style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, padding: "10px 6px", borderRadius: 10, cursor: "pointer", background: !currentTotem ? "#e7f2f8" : "#fff", border: `1.5px solid ${!currentTotem ? "var(--primary-blue)" : "#e3ebef"}` }}
              >
                <span style={{ width: 52, height: 52, display: "flex", alignItems: "center", justifyContent: "center", color: "#8a97a3", fontSize: 24 }}>∅</span>
                <span style={{ fontSize: 12, color: "#55707e" }}>{t('profile.totem.none')}</span>
              </button>
              {TOTEMS.map((tt) => (
                <button
                  key={tt.key}
                  onClick={() => saveTotem(tt.key)}
                  title={tt.name}
                  style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, padding: "10px 6px", borderRadius: 10, cursor: "pointer", background: currentTotem === tt.key ? "#e7f2f8" : "#fff", border: `1.5px solid ${currentTotem === tt.key ? "var(--primary-blue)" : "#e3ebef"}` }}
                >
                  <img src={totemUrl(tt.key)} alt="" style={{ width: 52, height: 52, objectFit: "contain" }} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#334", textAlign: "center", lineHeight: 1.15 }}>{tt.name}</span>
                  <span style={{ fontSize: 10.5, color: "#8a97a3", textAlign: "center", lineHeight: 1.1 }}>{tt.trait}</span>
                </button>
              ))}
            </div>
            <div style={{ textAlign: "right", marginTop: 10 }}>
              <button onClick={() => setEditingTotem(false)} style={{ background: "#e0e0e0", color: "#333", border: "none", borderRadius: 6, padding: "7px 14px", cursor: "pointer", fontWeight: "bold", fontSize: 13 }}>
                {t('common.close')}
              </button>
            </div>
          </div>
        )}
      </div>
      )}

      {/* --- Bloc description utilisateur --- */}
      <div style={{ marginBottom: "20px" }}>
        <h4
          style={{
            color: "var(--primary-blue)",
            marginBottom: "10px",
            textAlign: "center",
          }}
        >
          {t('profile.labels.description')}
        </h4>

        {!editingDesc ? (
          <div style={{ background: "#fff", padding: "14px 16px", borderRadius: "10px", boxShadow: "0 2px 5px rgba(0,0,0,0.05)" }}>
            {currentUserDescription && currentUserDescription.trim() ? (
              <p
                style={{ margin: 0, color: "#444", fontSize: "15px", lineHeight: 1.5, whiteSpace: "pre-wrap", overflowWrap: "anywhere", fontFamily: "inherit" }}
                dangerouslySetInnerHTML={{ __html: linkify(currentUserDescription) }}
              />
            ) : (
              <p style={{ margin: 0, color: "#999", fontStyle: "italic" }}>{t('profile.noDescription')}</p>
            )}
            <div style={{ textAlign: "center", marginTop: "12px" }}>
              <button onClick={() => setEditingDesc(true)} style={{ background: "none", color: "var(--primary-blue)", border: "1px solid var(--primary-blue)", borderRadius: "8px", padding: "7px 14px", cursor: "pointer", fontWeight: 600, fontSize: "13px" }}>
                {t('common.edit')}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ maxWidth: "100%", display: "flex", flexDirection: "column", alignItems: "stretch" }}>
            <textarea
              value={currentUserDescription}
              onChange={(e) => setCurrentUserDescription(e.target.value)}
              rows={4}
              style={{
                width: "100%", borderRadius: "6px", border: "1px solid #ccc", padding: "8px",
                fontSize: "14px", fontFamily: "inherit", resize: "vertical", background: "#fff",
                marginBottom: "10px", boxSizing: "border-box",
              }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
              <button onClick={() => setEditingDesc(false)} style={{ backgroundColor: "#e0e0e0", color: "#333", border: "none", borderRadius: "6px", padding: "8px 14px", cursor: "pointer", fontWeight: "bold", fontSize: "14px" }}>
                {t('common.cancel')}
              </button>
              <button onClick={saveDescription} style={{ backgroundColor: "var(--primary-blue)", color: "white", border: "none", borderRadius: "6px", padding: "8px 14px", cursor: "pointer", fontWeight: "bold", fontSize: "14px" }}>
                {t('common.save')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* --- Bloc certifications --- */}
      <div style={{ marginBottom: "20px" }}>
        <h4
          style={{
            color: "var(--primary-blue)",
            marginBottom: "10px",
            textAlign: "center",
          }}
        >
          {t('profile.labels.yourCertifications')}
        </h4>

        {!editingCerts ? (
          <div style={{ background: "#fff", padding: "14px", borderRadius: "10px", boxShadow: "0 2px 5px rgba(0,0,0,0.05)" }}>
            {selectedCerts.length > 0 ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", justifyContent: "center" }}>
                {selectedCerts.map((key) => (
                  <span key={key} style={{ background: "#e3f2fd", color: "var(--primary-blue)", border: "1px solid var(--primary-blue)", borderRadius: "999px", padding: "5px 12px", fontSize: "13px", fontWeight: 600 }}>
                    {getCertLabel(key)}
                  </span>
                ))}
              </div>
            ) : (
              <p style={{ color: "#888", textAlign: "center", margin: 0 }}>{t('profile.labels.noCertifications')}</p>
            )}
            <div style={{ textAlign: "center", marginTop: "12px" }}>
              <button onClick={() => setEditingCerts(true)} style={{ background: "none", color: "var(--primary-blue)", border: "1px solid var(--primary-blue)", borderRadius: "8px", padding: "7px 14px", cursor: "pointer", fontWeight: 600, fontSize: "13px" }}>
                {t('profile.buttons.editCertifications')}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "10px",
                background: "#fff",
                padding: "14px",
                borderRadius: "10px",
                boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
              }}
            >
              {CERT_OPTIONS.map(({ key, label }) => (
                <label
                  key={key}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "8px 10px",
                    borderRadius: "8px",
                    border: "1px solid #eee",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedCerts.includes(key)}
                    onChange={() => toggleCert(key)}
                  />
                  <span style={{ color: "#333", fontSize: "14px" }}>{label}</span>
                </label>
              ))}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: 12 }}>
              <button
                onClick={() => setEditingCerts(false)}
                style={{
                  backgroundColor: "#e0e0e0",
                  color: "#333",
                  border: "none",
                  borderRadius: "6px",
                  padding: "8px 14px",
                  cursor: "pointer",
                  fontWeight: "bold",
                  fontSize: "14px",
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={saveCertifications}
                disabled={savingCerts}
                style={{
                  backgroundColor: "var(--primary-blue)",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  padding: "8px 14px",
                  cursor: savingCerts ? "not-allowed" : "pointer",
                  fontWeight: "bold",
                  fontSize: "14px",
                  opacity: savingCerts ? 0.6 : 1,
                }}
              >
                {savingCerts ? t('profile.buttons.saving') : t('common.save')}
              </button>
            </div>
          </>
        )}
      </div>


      {/* --- Demandes d'amis reçues --- */}
      {pendingReceived.length > 0 && (
        <div
          style={{
            marginTop: "30px",
            marginBottom: "20px",
            background: "#fff8e6",
            padding: "15px 20px",
            borderRadius: "8px",
            boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
            border: "1px solid #ffe0a0",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              marginBottom: 10,
            }}
          >
            <h4 style={{ color: "#e67e00", margin: 0 }}>{t('friends.friendRequests')}</h4>
            <span style={{ fontSize: 12, color: "#e67e00" }}>{pendingReceived.length}</span>
          </div>

          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {pendingReceived.map((request) => {
              const requester = request.requester || {};
              return (
                <li
                  key={request.id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "10px 0",
                    borderBottom: "1px solid #ffe0a0",
                  }}
                >

                  <a href={`/profile/${requester.id}`}
                    style={{ textDecoration: "none", color: "#111", fontWeight: 500 }}
                  >
                    {requester.first_name || "—"} {requester.last_name || ""}
                  </a>

                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      onClick={() => handleAcceptRequest(request.id)}
                      style={{
                        backgroundColor: "var(--primary-blue)",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        padding: "6px 12px",
                        fontSize: "13px",
                        cursor: "pointer",
                      }}
                    >
                      {t('friends.buttons.accept')}
                    </button>
                    <button
                      onClick={() => handleRejectRequest(request.id)}
                      style={{
                        backgroundColor: "#888",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        padding: "6px 12px",
                        fontSize: "13px",
                        cursor: "pointer",
                      }}
                    >
                      {t('friends.buttons.reject')}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )
      }

      {/* --- Vos amis + recherche combinés --- */}
      <div
        style={{
          marginBottom: "20px",
          background: "#fff",
          padding: "15px 20px",
          borderRadius: "8px",
          boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            marginBottom: 10,
          }}
        >
          <h4 style={{ color: "var(--primary-blue)", margin: 0 }}>{t('friends.title')}</h4>
          {friends.length > 0 ? (
            <button
              onClick={() => setShowFriendsList((v) => !v)}
              style={{ background: "none", border: "none", color: "var(--primary-blue)", cursor: "pointer", fontSize: 13, fontWeight: 600, padding: 0 }}
            >
              {showFriendsList ? t('friends.hideList') : t('friends.showList', { count: friends.length })}
            </button>
          ) : (
            <span style={{ fontSize: 12, color: "#777" }}>{friends.length}</span>
          )}
        </div>


        <p style={{ color: "#555", fontSize: "13px", marginBottom: "12px" }}>
          {t('friends.searchDescription')}
        </p>

        {/* --- Barre de recherche --- */}
        <input
          type="text"
          placeholder={t('friends.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: "100%",
            padding: "8px",
            borderRadius: "6px",
            border: "1px solid #ccc",
            marginBottom: "15px",
          }}
        />

        {/* --- Résultats de recherche --- */}
        {searchTerm && (
          <div
            style={{
              background: "#f8f9fa",
              border: "1px solid #e9ecef",
              borderRadius: 10,
              padding: "10px 12px",
              marginBottom: 14,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <span style={{ fontWeight: 700, color: "#333" }}>
                {t('friends.searchTitle')}
              </span>
              <span style={{ fontSize: 12, color: "#777" }}>
                {filteredUsers.filter((u) => u.id !== currentUser?.id).length}
              </span>
            </div>

            {filteredUsers.filter((u) => u.id !== currentUser?.id).length > 0 ? (
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {filteredUsers
                  .filter((u) => u.id !== currentUser?.id)
                  .map((user) => {
                    const isAlreadyFriend = friendIds.has(user.id);
                    const hasPendingSent = pendingSentIds.has(user.id);
                    const hasPendingReceived = pendingReceivedIds.has(user.id);

                    return (
                      <li
                        key={user.id}
                        style={{
                          padding: "8px 0",
                          borderBottom: "1px solid #e9ecef",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span style={{ color: "#111" }}>
                          {user.first_name} {user.last_name}
                        </span>

                        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>

                          <a href={`/profile/${user.id}`}
                            style={{
                              color: "var(--primary-blue)",
                              textDecoration: "none",
                              fontSize: "13px",
                              padding: "4px 10px",
                              border: "1px solid var(--primary-blue)",
                              borderRadius: "6px",
                            }}
                          >
                            {t('common.viewProfile')}
                          </a>

                          {isAlreadyFriend ? (
                            <span style={{ fontSize: 12, color: "#2d6a4f", fontWeight: 700 }}>
                              ✓ {t('friends.alreadyFriend')}
                            </span>
                          ) : hasPendingSent ? (
                            <span
                              style={{
                                fontSize: 12,
                                color: "#888",
                                fontStyle: "italic",
                              }}
                            >
                              {t('friends.requestSent')}
                            </span>
                          ) : hasPendingReceived ? (
                            <span
                              style={{
                                fontSize: 12,
                                color: "#e67e00",
                                fontWeight: 600,
                              }}
                            >
                              {t('friends.sentYouRequest')} ↑
                            </span>
                          ) : (
                            <button
                              onClick={() =>
                                handleSendFriendRequest(
                                  user.id,
                                  `${user.first_name || ""} ${user.last_name || ""}`.trim()
                                )
                              }
                              style={{
                                backgroundColor: "var(--primary-blue)",
                                color: "white",
                                border: "none",
                                borderRadius: "6px",
                                padding: "4px 10px",
                                fontSize: "13px",
                                cursor: "pointer",
                              }}
                            >
                              {t('friends.sendRequest')}
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
              </ul>
            ) : (
              <div style={{ color: "#888", fontSize: 14, padding: "8px 0" }}>
                {t('friends.noSearchResults')}
              </div>
            )}
          </div>
        )}

        {/* --- Liste des amis (masquée par défaut) --- */}
        {friends.length === 0 ? (
          <p style={{ color: "#777", fontSize: "12px" }}>{t('friends.noFriends')}</p>
        ) : showFriendsList ? (
          <>
            {friends.length > 8 && (
              <input
                type="text"
                placeholder={t('friends.filterPlaceholder')}
                value={friendSearch}
                onChange={(e) => setFriendSearch(e.target.value)}
                style={{ width: "100%", padding: "8px 10px", marginBottom: "8px", borderRadius: "8px", border: "1px solid #ccc", boxSizing: "border-box" }}
              />
            )}
            <ul className="friends-list" style={{ listStyle: "none", paddingLeft: 0, margin: 0 }}>
              {(() => {
                const term = friendSearch.trim().toLowerCase();
                const filtered = friends.filter((f) => {
                  const fr = f.friend || {};
                  return !term || `${fr.first_name || ""} ${fr.last_name || ""}`.toLowerCase().includes(term);
                });
                const shown = (showAllFriends || term) ? filtered : filtered.slice(0, 8);
                if (shown.length === 0) {
                  return <li style={{ color: "#888", fontSize: 13, padding: "8px 4px" }}>{t('friends.noSearchResults')}</li>;
                }
                return shown.map((f) => {
              const friend = f.friend || {};
              return (
                <li
                  key={f.friend_id || f.id}
                  style={{
                    borderBottom: "1px solid #eee",
                    display: "flex",
                    flexWrap: "nowrap",
                    alignItems: "center",
                    padding: "8px 4px",
                    gap: "8px",
                  }}
                >
                  <span
                    style={{
                      color: "#111",
                      fontSize: "14px",
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {friend.first_name || "—"} {friend.last_name || ""}
                  </span>

                  <a
                    href={`/profile/${f.friend_id || friend.id}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      backgroundColor: "var(--primary-blue)",
                      color: "white",
                      textDecoration: "none",
                      fontSize: "14px",
                      fontWeight: "bold",
                      flexShrink: 0,
                    }}
                  >
                    ›
                  </a>

                  <button
                    onClick={() => setFriendToRemove({ friendshipId: f.id, name: friend.first_name || "" })}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      backgroundColor: "#e0e0e0",
                      color: "#666",
                      border: "none",
                      fontSize: "14px",
                      fontWeight: "bold",
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                  >
                    ×
                  </button>
                </li>
              );
                });
              })()}
            </ul>
            {!friendSearch.trim() && friends.length > 8 && (
              <div style={{ textAlign: "center", marginTop: "10px" }}>
                <button
                  onClick={() => setShowAllFriends((v) => !v)}
                  style={{ background: showAllFriends ? "#e0e0e0" : "var(--primary-blue)", color: showAllFriends ? "#333" : "#fff", border: "none", borderRadius: "6px", padding: "8px 16px", cursor: "pointer", fontWeight: 600, fontSize: "13px" }}
                >
                  {showAllFriends ? t('friends.showLess') : t('friends.showAll', { count: friends.length })}
                </button>
              </div>
            )}
          </>
        ) : null}

      </div>

      {/* --- Mes badges --- */}
      {renderBadgesSection(false)}

      {/* --- Mes clubs --- */}
      {renderClubsSection()}

      {/* --- Mes alertes --- */}
      <AlertPreferences />


      {/* --- Suppression de compte --- */}
      <div
        style={{
          marginTop: "40px",
          padding: "20px",
          background: "#fff5f5",
          borderRadius: "8px",
          border: "1px solid #ffcccc",
        }}
      >
        <h4 style={{ color: "#c00", margin: "0 0 10px 0" }}>{t('deleteAccount.title')}</h4>
        <p style={{ color: "#666", fontSize: "13px", marginBottom: "15px" }}>
          {t('deleteAccount.description')}
        </p>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button
            onClick={async () => {
              const confirmEmail = window.prompt(t('deleteAccount.confirmPrompt'));

              if (!confirmEmail) return;

              if (confirmEmail !== currentUser.email) {
                toast.error(t('deleteAccount.emailMismatch'));
                return;
              }

              try {
                const res = await fetchWithAuth(`${apiUrl}/users/${currentUser.id}`, {
                  method: "DELETE",
                  body: JSON.stringify({ confirmation_email: confirmEmail }),
                });

                if (!res.ok) {
                  const data = await res.json();
                  throw new Error(data.error || t('deleteAccount.deleteError'));
                }

                toast.success(t('deleteAccount.deleteSuccess'));

                setTimeout(() => {
                  localStorage.clear();
                  window.location.href = "/";
                }, 2000);
              } catch (err) {
                console.error(err);
                toast.error(err.message || t('deleteAccount.deleteErrorGeneric'));
              }
            }}
            style={{
              backgroundColor: "#c00",
              color: "white",
              border: "none",
              borderRadius: "6px",
              padding: "10px 20px",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            {t('common.delete')}
          </button>
        </div>
      </div>

      <div className="profile-bottom-spacer" />

      {
        showImageModal && (
          <div
            onClick={() => setShowImageModal(false)}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(0, 0, 0, 0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              cursor: "zoom-out",
            }}
          >
            <img
              src={zoomedImage}
              alt="Photo agrandie"
              style={{
                maxWidth: "30%",
                maxHeight: "40%",
                borderRadius: "12px",
                boxShadow: "0 6px 20px rgba(0,0,0,0.4)",
                transition: "transform 0.3s ease",
              }}
            />

          </div>
        )
      }

      <ToastContainer position="bottom-center" autoClose={3000} theme="colored" />
    </div >
  );
};

export default Profile;
